import { useMemo } from 'react';
import { format } from 'date-fns';
import { FillHeight } from '@emfood/shared';

import { Card, Button, useTheme, Typography, useMediaQuery } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel, GridPaginationModel } from '@mui/x-data-grid';

import { GenerationHistoryItem } from 'src/api/models/generation-history';

import { LoadingBarHorizontal } from 'src/components/loading';
import { DataGridNoRowsOverlay } from 'src/components/datagrid';

import GenerationHistoryMobile from './generation-history-mobile';

interface Props {
  generationHistory?: GenerationHistoryItem[];
  generationHistoryTotalCount?: number;
  // generationHistoryTotalPages: number;
  isLoading?: boolean;
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  onSortChanged: (model: GridSortModel) => void;
  onPaginationModelChanged: (model: GridPaginationModel) => void;
  onPreviewStory: (story: GenerationHistoryItem) => void;
}

export default function GenerationHistoryTable({
  generationHistory = [],
  isLoading = false,
  paginationModel,
  sortModel,
  generationHistoryTotalCount = 0,
  onSortChanged,
  onPaginationModelChanged,
  onPreviewStory,
}: Props) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Created Date',
        width: 200,
        sortable: false,
        minWidth: 180,
        valueFormatter: ({ value }) => format(new Date(value), 'MMM dd, yyyy HH:mm'),
      },
      {
        field: 'userFullName',
        headerName: 'User',
        sortable: false,
        flex: 1.5,
        renderCell: ({ row }) => (
          <Typography>{`${row.user.firstName} ${row.user.lastName}`}</Typography>
        ),
      },
      {
        field: 'product',
        headerName: 'Product',
        flex: 3.5,
        sortable: false,
        renderCell: ({ row }) => <Typography>{row.product?.name || ''}</Typography>,
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Button variant="outlined" size="small" onClick={() => onPreviewStory(row)}>
            Preview
          </Button>
        ),
      },
    ],
    [onPreviewStory]
  );

  if (isLoading) {
    return <LoadingBarHorizontal showLoading />;
  }

  if (!isDesktop) {
    return (
      <GenerationHistoryMobile
        generationHistory={generationHistory}
        generationHistoryTotalCount={generationHistoryTotalCount}
        isLoading={isLoading}
        paginationModel={paginationModel}
        onPaginationModelChanged={onPaginationModelChanged}
        onPreviewStory={onPreviewStory}
      />
    );
  }

  return (
    <Card>
      <FillHeight>
        <DataGrid
          checkboxSelection={false}
          disableRowSelectionOnClick
          rows={generationHistory}
          columns={columns}
          loading={isLoading}
          rowCount={generationHistoryTotalCount}
          pageSizeOptions={[5, 10, 25]}
          paginationModel={paginationModel}
          paginationMode="server"
          onPaginationModelChange={onPaginationModelChanged}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={onSortChanged}
          slots={{
            noRowsOverlay: () => <DataGridNoRowsOverlay overlayText="No Stories" />,
            noResultsOverlay: () => <DataGridNoRowsOverlay overlayText="No Stories" />,
          }}
          sx={{
            '.MuiDataGrid-overlayWrapper': { height: '200px' },
            '& .MuiDataGrid-cell': {
              py: 1,
            },
          }}
          hideFooterSelectedRowCount
          disableColumnMenu
        />
      </FillHeight>
    </Card>
  );
}
